import { computed } from 'vue';

/**
 * @typedef {Object} Attribute
 * @property {string} attribute_description
 * @property {string} attribute_display_name
 * @property {string} attribute_display_type
 * @property {string} attribute_key
 * @property {'contact_attribute' | 'conversation_attribute'} attribute_model
 * @property {Array} attribute_values
 * @property {string} created_at
 * @property {any | null} default_value
 * @property {number} id
 * @property {string | null} regex_cue
 * @property {string | null} regex_pattern
 * @property {string} updated_at
 * @property {Object | number | string | undefined | null} value
 */

/**
 * Composable para formatação de atributos customizados
 * @param {import('vue').Ref<Attribute[]>} attributes - Reactive reference dos atributos
 * @param {import('vue').Ref<Object>} customAttributes - Reactive reference dos atributos customizados
 * @param {string} attributeType - Tipo do atributo
 */
export function useCustomAttributesFormatter(attributes, customAttributes, attributeType) {
  /**
   * @param {string | number | boolean} attributeValue
   * @returns {string}
   */
  const attributeDisplayType = (attributeValue) => {
    const isNumber = (value) => {
      return Number.isInteger(Number(value)) && Number(value) > 0;
    };

    const isValidURL = (string) => {
      try {
        // eslint-disable-next-line no-new
        new URL(string);
      } catch (_) {
        return false;
      }
      return true;
    };

    if (isNumber(attributeValue)) {
      return 'number';
    }
    if (isValidURL(attributeValue)) {
      return 'link';
    }
    return 'text';
  };

  /**
   * @param {Attribute[]} attributes
   * @param {string} key
   * @returns {Attribute | undefined}
   */
  const findAttributeByKey = (attributes, key) => {
    return attributes.find(({ attribute_key }) => attribute_key === key);
  };

  /**
   * @param {Attribute | undefined} item
   * @param {string} key
   * @param {string} value
   * @returns {Attribute}
   */
  const formatAttribute = (item, key, value) => {
    if (item) {
      return {
        ...item,
        value: value,
      };
    }

    return {
      value: value,
      attribute_description: key,
      attribute_display_name: key,
      attribute_display_type: attributeDisplayType(value),
      attribute_key: key,
      attribute_model: attributeType,
      id: Math.random(),
    };
  };

  /**
   * @param {Attribute[]} attributes
   * @returns {Attribute[]} Array of attributes without duplicates based on attribute_key
   */
  const removeDuplicateAttributes = (attributes) => {
    /** @type {string[]} */
    const seenKeys = [];

    return attributes.filter(({ attribute_key }) => {
      if (seenKeys.includes(attribute_key)) return false;

      seenKeys.push(attribute_key);
      return true;
    });
  };

  /**
   * Computed property que retorna os atributos customizados formatados
   * @returns {import('vue').ComputedRef<Attribute[]>}
   */
  const formattedCustomAttributes = computed(() => {
    return Object.entries(customAttributes.value).map(([key, value]) => {
      const item = findAttributeByKey(attributes.value, key);
      return formatAttribute(item, key, value);
    });
  });

  // Retorna todas as funções e propriedades computadas
  return {
    formattedCustomAttributes,
    findAttributeByKey,
    formatAttribute,
    removeDuplicateAttributes,
    attributeDisplayType,
  };
}